import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function Reports() {
  const [fdsData, setFdsData] = useState([]);
  const [stationData, setStationData] = useState([]);
  const [wayleaveData, setWayleaveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Filter states for Damaged Station report
  const [reportFilter, setReportFilter] = useState('all'); // 'all', 'stations', 'daterange'
  const [selectedStations, setSelectedStations] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Filter states for Wayleave report
  const [wayleaveFilter, setWayleaveFilter] = useState('all'); // 'all', 'stations', 'daterange'
  const [selectedWayleaveStations, setSelectedWayleaveStations] = useState([]);
  const [wayleaveStartDate, setWayleaveStartDate] = useState('');
  const [wayleaveEndDate, setWayleaveEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch damaged station data
      const fdsResponse = await axios.get('/api/locations/fds/all', { withCredentials: true });
      setFdsData(fdsResponse.data);

      // Fetch station info
      const stationResponse = await axios.get('/api/stations/all', { withCredentials: true });
      setStationData(stationResponse.data);

      // Fetch wayleave report data
      const wayleaveResponse = await axios.get('/api/locations/wayleave/report', { withCredentials: true });
      setWayleaveData(wayleaveResponse.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const resetFilters = () => {
    setSelectedStations([]);
    setStartDate('');
    setEndDate('');
    setReportFilter('all');
  };

  const resetWayleaveFilters = () => {
    setSelectedWayleaveStations([]);
    setWayleaveStartDate('');
    setWayleaveEndDate('');
    setWayleaveFilter('all');
  };

  // Calculate days between two dates
  const daysBetween = (date1, date2) => {
    if (!date1 || !date2) return null;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get the current milestone and total days for a wayleave record
  const getWayleaveProgress = (record) => {
    const milestones = [
      { field: 'HODocsReceived', label: 'HO Docs Received' },
      { field: 'HODocsChecked', label: 'HO Docs Checked' },
      { field: 'HODocsSubmitted', label: 'HO Docs Submitted' },
      { field: 'WCGApprovalObtained', label: 'WCG Approval Obtained' },
      { field: 'AsBuiltSubmitted', label: 'As-Built Submitted' }
    ];

    let lastCompletedIndex = -1;
    let firstDate = null;
    let lastDate = null;

    for (let i = 0; i < milestones.length; i++) {
      const date = record[milestones[i].field];
      if (date) {
        if (firstDate === null) firstDate = date;
        lastDate = date;
        lastCompletedIndex = i;
      }
    }

    const currentMilestone = lastCompletedIndex >= 0 ? milestones[lastCompletedIndex].label : 'Not Started';
    const totalDays = firstDate && lastDate ? daysBetween(firstDate, lastDate) : 0;

    return { currentMilestone, totalDays, completedSteps: lastCompletedIndex + 1 };
  };

  // Filter wayleave data based on selected filters
  const getFilteredWayleaveData = () => {
    let filtered = [...wayleaveData];

    // Apply station filter
    if (wayleaveFilter === 'stations' && selectedWayleaveStations.length > 0) {
      filtered = filtered.filter(record => selectedWayleaveStations.includes(record.StationNo));
    }

    // Apply date range filter (based on first milestone date - HODocsReceived)
    if (wayleaveFilter === 'daterange' && (wayleaveStartDate || wayleaveEndDate)) {
      filtered = filtered.filter(record => {
        // Find the first date in any milestone
        const dates = [
          record.HODocsReceived,
          record.HODocsChecked,
          record.HODocsSubmitted,
          record.WCGApprovalObtained,
          record.AsBuiltSubmitted
        ].filter(d => d);

        if (dates.length === 0) return false;

        const firstRecordDate = new Date(Math.min(...dates.map(d => new Date(d))));
        const start = wayleaveStartDate ? new Date(wayleaveStartDate) : null;
        const end = wayleaveEndDate ? new Date(wayleaveEndDate) : null;

        if (start && end) {
          return firstRecordDate >= start && firstRecordDate <= end;
        } else if (start) {
          return firstRecordDate >= start;
        } else if (end) {
          return firstRecordDate <= end;
        }
        return true;
      });
    }

    // Sort by total days ascending (0 days / no progress first, most days last)
    filtered.sort((a, b) => {
      const progressA = getWayleaveProgress(a);
      const progressB = getWayleaveProgress(b);
      return progressA.totalDays - progressB.totalDays;
    });

    return filtered;
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilteredData = () => {
    let filtered = [...fdsData];

    // Apply station filter
    if (reportFilter === 'stations' && selectedStations.length > 0) {
      filtered = filtered.filter(record => selectedStations.includes(record.StationNo));
    }

    // Apply date range filter
    if (reportFilter === 'daterange' && (startDate || endDate)) {
      filtered = filtered.filter(record => {
        if (!record.Date) return false;
        const recordDate = new Date(record.Date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return recordDate >= start && recordDate <= end;
        } else if (start) {
          return recordDate >= start;
        } else if (end) {
          return recordDate <= end;
        }
        return true;
      });
    }

    return filtered;
  };

  const exportDamagedStationCSV = () => {
    setGenerating(true);

    try {
      // Get filtered data
      const filteredData = getFilteredData();

      if (filteredData.length === 0) {
        alert('No data matches the selected filters');
        setGenerating(false);
        return;
      }

      // Sort data by station number, then by date
      const sortedData = filteredData.sort((a, b) => {
        const stationCompare = String(a.StationNo || '').localeCompare(String(b.StationNo || ''));
        if (stationCompare !== 0) return stationCompare;

        // Sort by date (newest first)
        const dateA = a.Date ? new Date(a.Date) : null;
        const dateB = b.Date ? new Date(b.Date) : null;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB - dateA;
      });

      // Create CSV headers
      const headers = ['Station No.', 'Owner', 'Description', 'Damage Type', 'Date', 'Affected Components', 'Remedial Action'];

      // Create CSV rows
      const rows = sortedData.map(record => [
        record.StationNo || '',
        record.Owner || '',
        record.Description || '',
        record.DamageType || '',
        formatDate(record.Date),
        record.AffectedComponents || '',
        record.RemedialAction || ''
      ]);

      // Escape CSV fields (handle commas and quotes)
      const escapeCSV = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      // Build CSV content
      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      downloadCSV(csvContent, `Damaged_Stations_Report_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('Error generating CSV:', err);
      alert('Failed to generate CSV report');
    } finally {
      setGenerating(false);
    }
  };

  const exportDamageTypeCSV = () => {
    setGenerating(true);

    try {
      // Group by damage type
      const damageTypeCounts = {};
      fdsData.forEach(record => {
        const damageType = record.DamageType || 'Unknown';
        damageTypeCounts[damageType] = (damageTypeCounts[damageType] || 0) + 1;
      });

      // Sort by count (descending)
      const sortedDamageTypes = Object.entries(damageTypeCounts).sort((a, b) => b[1] - a[1]);

      // Create CSV headers
      const headers = ['Damage Type', 'Count', 'Percentage'];

      // Create CSV rows
      const rows = sortedDamageTypes.map(([type, count]) => {
        const percentage = ((count / fdsData.length) * 100).toFixed(1);
        return [type, count, `${percentage}%`];
      });

      // Escape CSV fields
      const escapeCSV = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      // Build CSV content
      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      downloadCSV(csvContent, `Damage_Type_Analysis_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('Error generating CSV:', err);
      alert('Failed to generate CSV report');
    } finally {
      setGenerating(false);
    }
  };

  const generateDamagedStationReport = async () => {
    setGenerating(true);

    try {
      // Get filtered data
      const filteredData = getFilteredData();

      if (filteredData.length === 0) {
        alert('No data matches the selected filters');
        setGenerating(false);
        return;
      }

      // Create new PDF document
      const doc = new jsPDF();

      // Load and add logo to top right
      try {
        const logoImg = await fetch('/logo.png');
        const logoBlob = await logoImg.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoDataUrl, 'PNG', 160, 10, 35, 15);
      } catch (logoErr) {
        console.warn('Could not load logo:', logoErr);
      }

      // Add title
      doc.setFontSize(18);
      doc.text('Damaged Station Report', 14, 20);

      // Add metadata
      doc.setFontSize(10);
      doc.text(`Total Damage Records: ${filteredData.length}`, 14, 28);

      // Group data by station number
      const stationGroups = {};
      filteredData.forEach(record => {
        const stationNo = record.StationNo || 'Unknown';
        if (!stationGroups[stationNo]) {
          stationGroups[stationNo] = {
            stationNo: stationNo,
            description: record.Description || '-',
            owner: record.Owner || '-',
            damages: []
          };
        }
        stationGroups[stationNo].damages.push({
          damageType: record.DamageType || '-',
          date: formatDate(record.Date),
          dateValue: record.Date ? new Date(record.Date) : null,
          affectedComponents: record.AffectedComponents || '-',
          remedialAction: record.RemedialAction || '-'
        });
      });

      // Sort stations by number and sort damages by date within each station
      const sortedStations = Object.values(stationGroups)
        .sort((a, b) => String(a.stationNo).localeCompare(String(b.stationNo)))
        .map(station => {
          // Sort damages by date (newest first)
          station.damages.sort((a, b) => {
            // Handle null dates (put them at the end)
            if (!a.dateValue && !b.dateValue) return 0;
            if (!a.dateValue) return 1;
            if (!b.dateValue) return -1;
            // Sort descending (newest first)
            return b.dateValue - a.dateValue;
          });
          return station;
        });

      // Add station count
      doc.text(`Total Unique Stations: ${sortedStations.length}`, 14, 34);

      const reportDate = formatDate(new Date());
      const reportName = 'Damaged Station Report';

      // Footer function
      const addFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100);

          // Page number (left)
          doc.text(`Page ${i} of ${pageCount}`, 14, 285);

          // Report generated date (center)
          doc.text(`Generated: ${reportDate}`, 105, 285, { align: 'center' });

          // Report name (right)
          doc.text(reportName, 196, 285, { align: 'right' });
        }
      };

      let currentY = 42;

      // Add each station as a section
      sortedStations.forEach((station, index) => {
        // Check if we need a new page
        if (currentY > 250) {
          doc.addPage();
          currentY = 20;
        }

        // Station header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(37, 99, 235);
        doc.text(`Station ${station.stationNo}`, 14, currentY);

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        currentY += 5;
        doc.text(`Owner: ${station.owner} | Description: ${station.description}`, 14, currentY);
        currentY += 2;

        // Damage records table for this station
        const damageTableData = station.damages.map(d => [
          d.damageType,
          d.date,
          d.affectedComponents,
          d.remedialAction
        ]);

        autoTable(doc, {
          startY: currentY,
          head: [['Damage Type', 'Date', 'Affected Components', 'Remedial Action']],
          body: damageTableData,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: {
            fillColor: [229, 231, 235],
            textColor: [31, 41, 55],
            fontStyle: 'bold',
            fontSize: 8
          },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 25 },
            2: { cellWidth: 55 },
            3: { cellWidth: 55 }
          },
          margin: { left: 14, right: 14 },
          didDrawPage: (data) => {
            currentY = data.cursor.y;
          }
        });

        currentY = doc.lastAutoTable.finalY + 5;
      });

      // Add footer to all pages
      addFooter(doc);

      // Save the PDF
      doc.save(`Damaged_Stations_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGenerating(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const generateStationSummaryReport = async () => {
    setGenerating(true);

    try {
      const doc = new jsPDF();

      // Load and add logo to top right
      try {
        const logoImg = await fetch('/logo.png');
        const logoBlob = await logoImg.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoDataUrl, 'PNG', 160, 10, 35, 15);
      } catch (logoErr) {
        console.warn('Could not load logo:', logoErr);
      }

      doc.setFontSize(18);
      doc.text('Station Summary Report', 14, 20);

      doc.setFontSize(10);
      doc.text(`Total Stations: ${stationData.length}`, 14, 28);

      const reportDate = formatDate(new Date());
      const reportName = 'Station Summary Report';

      // Footer function
      const addFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(`Page ${i} of ${pageCount}`, 14, 285);
          doc.text(`Generated: ${reportDate}`, 105, 285, { align: 'center' });
          doc.text(reportName, 196, 285, { align: 'right' });
        }
      };

      // Sort by station number
      const sortedStations = [...stationData].sort((a, b) =>
        String(a.STATION_NO || '').localeCompare(String(b.STATION_NO || ''))
      );

      const tableData = sortedStations.map(station => [
        station.STATION_NO || '-',
        station.Owner || '-',
        station.Route || '-',
        station.Road || '-',
        station.Description || '-',
        station.Stratum || '-',
        station.Lanes || '-'
      ]);

      autoTable(doc, {
        startY: 34,
        head: [['Station No.', 'Owner', 'Route', 'Road', 'Description', 'Stratum', 'Lanes']],
        body: tableData,
        theme: 'striped',
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 35 },
          4: { cellWidth: 50 },
          5: { cellWidth: 20 },
          6: { cellWidth: 12 }
        },
        margin: { top: 34 }
      });

      // Add footer to all pages
      addFooter(doc);

      doc.save(`Station_Summary_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGenerating(false);
    }
  };

  const generateDamageTypeReport = async () => {
    setGenerating(true);

    try {
      const doc = new jsPDF();

      // Load and add logo to top right
      try {
        const logoImg = await fetch('/logo.png');
        const logoBlob = await logoImg.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoDataUrl, 'PNG', 160, 10, 35, 15);
      } catch (logoErr) {
        console.warn('Could not load logo:', logoErr);
      }

      doc.setFontSize(18);
      doc.text('Damage Type Analysis Report', 14, 20);

      doc.setFontSize(10);

      // Group by damage type
      const damageTypeCounts = {};
      fdsData.forEach(record => {
        const damageType = record.DamageType || 'Unknown';
        damageTypeCounts[damageType] = (damageTypeCounts[damageType] || 0) + 1;
      });

      const reportDate = formatDate(new Date());
      const reportName = 'Damage Type Analysis Report';

      // Footer function
      const addFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(`Page ${i} of ${pageCount}`, 14, 285);
          doc.text(`Generated: ${reportDate}`, 105, 285, { align: 'center' });
          doc.text(reportName, 196, 285, { align: 'right' });
        }
      };

      // Sort by count (descending)
      const sortedDamageTypes = Object.entries(damageTypeCounts).sort((a, b) => b[1] - a[1]);

      doc.text(`Total Damage Types: ${sortedDamageTypes.length}`, 14, 28);
      doc.text(`Total Records: ${fdsData.length}`, 14, 34);

      const tableData = sortedDamageTypes.map(([type, count]) => {
        const percentage = ((count / fdsData.length) * 100).toFixed(1);
        return [type, count, `${percentage}%`];
      });

      autoTable(doc, {
        startY: 42,
        head: [['Damage Type', 'Count', 'Percentage']],
        body: tableData,
        theme: 'striped',
        styles: {
          fontSize: 10,
          cellPadding: 4
        },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 40, halign: 'center' },
          2: { cellWidth: 40, halign: 'center' }
        },
        margin: { top: 42 }
      });

      // Add footer to all pages
      addFooter(doc);

      doc.save(`Damage_Type_Analysis_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGenerating(false);
    }
  };

  const generateWayleaveReport = async () => {
    setGenerating(true);

    try {
      const filteredData = getFilteredWayleaveData();

      if (filteredData.length === 0) {
        alert('No data matches the selected filters');
        setGenerating(false);
        return;
      }

      const doc = new jsPDF('landscape');

      // Load and add logo
      try {
        const logoImg = await fetch('/logo.png');
        const logoBlob = await logoImg.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoDataUrl, 'PNG', 240, 10, 35, 15);
      } catch (logoErr) {
        console.warn('Could not load logo:', logoErr);
      }

      doc.setFontSize(18);
      doc.text('Wayleave Application Progress Report', 14, 20);

      doc.setFontSize(10);
      doc.text(`Total Applications: ${filteredData.length}`, 14, 28);

      // Get unique stations count
      const uniqueStations = [...new Set(filteredData.map(r => r.StationNo))];
      doc.text(`Unique Stations: ${uniqueStations.length}`, 14, 34);

      const reportDate = formatDate(new Date());
      const reportName = 'Wayleave Application Progress Report';

      // Footer function
      const addFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(`Page ${i} of ${pageCount}`, 14, 200);
          doc.text(`Generated: ${reportDate}`, 148, 200, { align: 'center' });
          doc.text(reportName, 283, 200, { align: 'right' });
        }
      };

      // Group by station
      const stationGroups = {};
      filteredData.forEach(record => {
        const stationNo = record.StationNo || 'Unknown';
        if (!stationGroups[stationNo]) {
          stationGroups[stationNo] = {
            stationNo: stationNo,
            owner: record.Owner || '-',
            description: record.Description || '-',
            road: record.Road || '-',
            applications: [],
            maxTotalDays: 0
          };
        }
        stationGroups[stationNo].applications.push(record);
        // Track max total days for this station (for sorting)
        const progress = getWayleaveProgress(record);
        if (progress.totalDays > stationGroups[stationNo].maxTotalDays) {
          stationGroups[stationNo].maxTotalDays = progress.totalDays;
        }
      });

      // Sort by total days ascending (0 days / no progress first)
      const sortedStations = Object.values(stationGroups)
        .sort((a, b) => a.maxTotalDays - b.maxTotalDays);

      let currentY = 42;

      sortedStations.forEach((station) => {
        // Check if we need a new page
        if (currentY > 170) {
          doc.addPage();
          currentY = 20;
        }

        // Station header
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(37, 99, 235);
        doc.text(`Station ${station.stationNo}`, 14, currentY);

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        currentY += 5;
        doc.text(`Owner: ${station.owner} | Description: ${station.description} | Road: ${station.road}`, 14, currentY);
        currentY += 2;

        // Build table data for this station's applications
        const tableData = station.applications.map(app => {
          const days1 = daysBetween(app.HODocsReceived, app.HODocsChecked);
          const days2 = daysBetween(app.HODocsChecked, app.HODocsSubmitted);
          const days3 = daysBetween(app.HODocsSubmitted, app.WCGApprovalObtained);
          const days4 = daysBetween(app.WCGApprovalObtained, app.AsBuiltSubmitted);
          const progress = getWayleaveProgress(app);

          return [
            formatDate(app.HODocsReceived),
            days1 !== null ? `${days1}d` : '-',
            formatDate(app.HODocsChecked),
            days2 !== null ? `${days2}d` : '-',
            formatDate(app.HODocsSubmitted),
            days3 !== null ? `${days3}d` : '-',
            formatDate(app.WCGApprovalObtained),
            days4 !== null ? `${days4}d` : '-',
            formatDate(app.AsBuiltSubmitted),
            `${progress.totalDays}d`
          ];
        });

        autoTable(doc, {
          startY: currentY,
          head: [[
            'HO Docs Received', 'Days',
            'HO Docs Checked', 'Days',
            'HO Docs Submitted', 'Days',
            'WCG Approval', 'Days',
            'As-Built Submitted', 'Total Days'
          ]],
          body: tableData,
          theme: 'grid',
          styles: {
            fontSize: 7,
            cellPadding: 1.5
          },
          headStyles: {
            fillColor: [229, 231, 235],
            textColor: [31, 41, 55],
            fontStyle: 'bold',
            fontSize: 7
          },
          columnStyles: {
            0: { cellWidth: 24 },
            1: { cellWidth: 12, halign: 'center' },
            2: { cellWidth: 24 },
            3: { cellWidth: 12, halign: 'center' },
            4: { cellWidth: 24 },
            5: { cellWidth: 12, halign: 'center' },
            6: { cellWidth: 24 },
            7: { cellWidth: 12, halign: 'center' },
            8: { cellWidth: 24 },
            9: { cellWidth: 18, halign: 'center', fontStyle: 'bold' }
          },
          margin: { left: 14, right: 14 }
        });

        currentY = doc.lastAutoTable.finalY + 8;
      });

      addFooter(doc);
      doc.save(`Wayleave_Progress_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGenerating(false);
    }
  };

  const exportWayleaveCSV = () => {
    setGenerating(true);

    try {
      const filteredData = getFilteredWayleaveData();

      if (filteredData.length === 0) {
        alert('No data matches the selected filters');
        setGenerating(false);
        return;
      }

      // Data is already sorted by total days (ascending) from getFilteredWayleaveData()
      const sortedData = filteredData;

      const headers = [
        'Station No.', 'Owner', 'Description', 'Road',
        'HO Docs Received', 'Days to Checked',
        'HO Docs Checked', 'Days to Submitted',
        'HO Docs Submitted', 'Days to Approval',
        'WCG Approval Obtained', 'Days to As-Built',
        'As-Built Submitted', 'Total Days', 'Current Status'
      ];

      const rows = sortedData.map(record => {
        const days1 = daysBetween(record.HODocsReceived, record.HODocsChecked);
        const days2 = daysBetween(record.HODocsChecked, record.HODocsSubmitted);
        const days3 = daysBetween(record.HODocsSubmitted, record.WCGApprovalObtained);
        const days4 = daysBetween(record.WCGApprovalObtained, record.AsBuiltSubmitted);
        const progress = getWayleaveProgress(record);

        return [
          record.StationNo || '',
          record.Owner || '',
          record.Description || '',
          record.Road || '',
          formatDate(record.HODocsReceived),
          days1 !== null ? days1 : '',
          formatDate(record.HODocsChecked),
          days2 !== null ? days2 : '',
          formatDate(record.HODocsSubmitted),
          days3 !== null ? days3 : '',
          formatDate(record.WCGApprovalObtained),
          days4 !== null ? days4 : '',
          formatDate(record.AsBuiltSubmitted),
          progress.totalDays,
          progress.currentMilestone
        ];
      });

      const escapeCSV = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      downloadCSV(csvContent, `Wayleave_Progress_Report_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('Error generating CSV:', err);
      alert('Failed to generate CSV report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-gray-600">Loading report data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Damaged Station Report */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">Damaged Stations</h3>
              <p className="text-sm text-gray-500">{fdsData.length} records</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Comprehensive report of all damaged stations, sorted by station number and damage type.
          </p>

          {/* Filter Options */}
          <div className="mb-4 space-y-3">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setReportFilter('all')}
                className={`px-3 py-1 text-xs rounded ${reportFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                All Stations
              </button>
              <button
                onClick={() => setReportFilter('stations')}
                className={`px-3 py-1 text-xs rounded ${reportFilter === 'stations' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Select Stations
              </button>
              <button
                onClick={() => setReportFilter('daterange')}
                className={`px-3 py-1 text-xs rounded ${reportFilter === 'daterange' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                By Date Range
              </button>
              {(reportFilter === 'stations' || reportFilter === 'daterange') && (
                <button
                  onClick={resetFilters}
                  className="ml-auto px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                  title="Reset filters"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
            </div>

            {reportFilter === 'stations' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Stations</label>
                <select
                  multiple
                  value={selectedStations}
                  onChange={(e) => setSelectedStations(Array.from(e.target.selectedOptions, option => option.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm h-24"
                >
                  {(() => {
                    // Calculate record counts per station
                    const stationCounts = {};
                    fdsData.forEach(record => {
                      const station = record.StationNo;
                      stationCounts[station] = (stationCounts[station] || 0) + 1;
                    });

                    // Get unique stations and sort them
                    const uniqueStations = [...new Set(fdsData.map(r => r.StationNo))].sort((a, b) => String(a).localeCompare(String(b)));

                    return uniqueStations.map(station => (
                      <option key={station} value={station}>
                        {station} ({stationCounts[station]} {stationCounts[station] === 1 ? 'record' : 'records'})
                      </option>
                    ));
                  })()}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple stations</p>
              </div>
            )}

            {reportFilter === 'daterange' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={generateDamagedStationReport}
              disabled={generating || fdsData.length === 0}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {generating ? 'Generating...' : 'Export PDF'}
            </button>
            <button
              onClick={exportDamagedStationCSV}
              disabled={generating || fdsData.length === 0}
              className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        {/* Damage Type Analysis */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">Damage Analysis</h3>
              <p className="text-sm text-gray-500">By damage type</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Statistical breakdown of damage types with counts and percentages.
          </p>
          <div className="flex gap-2">
            <button
              onClick={generateDamageTypeReport}
              disabled={generating || fdsData.length === 0}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {generating ? 'Generating...' : 'Export PDF'}
            </button>
            <button
              onClick={exportDamageTypeCSV}
              disabled={generating || fdsData.length === 0}
              className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        {/* Wayleave Report */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">Wayleave Report</h3>
              <p className="text-sm text-gray-500">{wayleaveData.length} applications</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Track wayleave application progress with milestone dates and processing times.
          </p>

          {/* Filter Options */}
          <div className="mb-4 space-y-3">
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setWayleaveFilter('all')}
                className={`px-3 py-1 text-xs rounded ${wayleaveFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                All Stations
              </button>
              <button
                onClick={() => setWayleaveFilter('stations')}
                className={`px-3 py-1 text-xs rounded ${wayleaveFilter === 'stations' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Select Stations
              </button>
              <button
                onClick={() => setWayleaveFilter('daterange')}
                className={`px-3 py-1 text-xs rounded ${wayleaveFilter === 'daterange' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                By Date Range
              </button>
              {(wayleaveFilter === 'stations' || wayleaveFilter === 'daterange') && (
                <button
                  onClick={resetWayleaveFilters}
                  className="ml-auto px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                  title="Reset filters"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
            </div>

            {wayleaveFilter === 'stations' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Stations</label>
                <select
                  multiple
                  value={selectedWayleaveStations}
                  onChange={(e) => setSelectedWayleaveStations(Array.from(e.target.selectedOptions, option => option.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm h-24"
                >
                  {(() => {
                    // Calculate application counts per station
                    const stationCounts = {};
                    wayleaveData.forEach(record => {
                      const station = record.StationNo;
                      stationCounts[station] = (stationCounts[station] || 0) + 1;
                    });

                    // Get unique stations and sort them
                    const uniqueStations = [...new Set(wayleaveData.map(r => r.StationNo))].sort((a, b) => String(a).localeCompare(String(b)));

                    return uniqueStations.map(station => (
                      <option key={station} value={station}>
                        {station} ({stationCounts[station]} {stationCounts[station] === 1 ? 'application' : 'applications'})
                      </option>
                    ));
                  })()}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple stations</p>
              </div>
            )}

            {wayleaveFilter === 'daterange' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={wayleaveStartDate}
                    onChange={(e) => setWayleaveStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={wayleaveEndDate}
                    onChange={(e) => setWayleaveEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={generateWayleaveReport}
              disabled={generating || wayleaveData.length === 0}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {generating ? 'Generating...' : 'Export PDF'}
            </button>
            <button
              onClick={exportWayleaveCSV}
              disabled={generating || wayleaveData.length === 0}
              className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
